import {
  MOBILE_OS,
  SUPPORTED_BROWSERS,
  APPLE_APP_LINK_PATHS
} from '@strava/constants/src/appConstants';
import { isString } from 'lodash-es';
import camelCase from 'lodash-es/camelCase';
import isObject from 'lodash-es/isObject';

export const generateRandomId = () => Math.random().toString(36).substring(2);

// Capitalizes all the words and replaces some characters in the string to create a nicer looking title.
// eg. 'man from the boondocks' => 'Man From The Boondocks'
const titleize = (str) =>
  str.replace(
    /\w\S*/g,
    (txt) => txt.charAt(0).toUpperCase() + txt.substr(1).toLowerCase()
  );

export const displayName = (
  { firstName = '', lastName = '' },
  options = { forceAnonymize: false, maxLength: 100 }
) => {
  const { maxLength, forceAnonymize } = options;
  const fullName = `${firstName} ${lastName}`;

  if (forceAnonymize || fullName.length > maxLength) {
    if (firstName.length > maxLength - 3) {
      return `${titleize(firstName.substring(0, maxLength - 4))}... ${lastName
        .charAt(0)
        .toUpperCase()}.`;
    }
    return `${titleize(firstName)} ${lastName.charAt(0).toUpperCase()}.`;
  }
  return fullName.trim();
};

/**
 * Checks to see if device is mobile
 * @param {string} userAgent - user agent string for the current browser. For next-js apps (SSR),
 *       this value can be retrieved from request.headers['user-agent']
 * @returns {boolean} - true if the device is mobile
 */
export const isMobile = (userAgent = window?.navigator?.userAgent) => {
  if (isString(userAgent)) {
    // ref - https://developer.mozilla.org/en-US/docs/Web/HTTP/Browser_detection_using_the_user_agent#mobile_tablet_or_desktop
    return userAgent.toLowerCase().includes('mobi');
  }
  return false;
};

/**
 * Determines the OS for mobile devices
 * @param {string} userAgent - user agent string for the current browser. For next-js apps (SSR),
 *       this value can be retrieved from request.headers['user-agent']
 * @returns {(string|null)} - one of ['android', 'ipad', 'iphone'] or null for unknown
 */
export const getMobileOS = (userAgent = window?.navigator?.userAgent) => {
  if (isString(userAgent)) {
    if (userAgent.includes('Android')) {
      return MOBILE_OS.android;
    }
    if (userAgent.includes('iPad')) {
      return MOBILE_OS.iPad;
    }
    if (userAgent.includes('iPhone')) {
      return MOBILE_OS.iPhone;
    }
  }
  return null;
};

/**
 * Determines, in a limited fashion, the browser according to the user agent.
 * NOTE: UAs are notoriously difficult to parse. This is meant as a bare-bones, noncomprehensive parser,
 * based on https://www.whatismybrowser.com/guides/the-latest-user-agent/.
 *
 * The order of conditionals is important, bc some Chrome & Firefox UAs include 'Safari', too.
 * Some Edge UAs may be recognized as Chrome for now, since they can include 'Chrome'. We don't
 * officially support Edge (yet?).
 *
 * @param {string} userAgent - user agent string for the current browser. For next-js apps (SSR),
 *       this value can be retrieved from request.headers['user-agent']
 * @returns {(string|null)} - one of ['chrome', 'firefox', 'safari'] or null for unknown/unsupported browser
 */
export const getBrowser = (userAgent = window?.navigator?.userAgent) => {
  if (isString(userAgent)) {
    if (userAgent.includes('Chrome') || userAgent.includes('CriOS')) {
      return SUPPORTED_BROWSERS.chrome;
    }
    if (userAgent.includes('Firefox') || userAgent.includes('FxiOS'))
      return SUPPORTED_BROWSERS.firefox;
    if (userAgent.includes('Safari')) {
      return SUPPORTED_BROWSERS.safari;
    }
  }

  return null;
};

/**
 * Universal links are determined by the paths described in `apple-app-site-association` in active.
 * @param {string} pathname - window.location.pathname or nextjs equivalent
 * @returns {boolean} - whether the pathname string matches
 */
export const isUniversalLinkPage = (pathname) => {
  if (isString(pathname)) {
    return APPLE_APP_LINK_PATHS.some((path) => {
      const pathWithoutAsterisk = path.replace('/*', '');
      return pathname.startsWith(pathWithoutAsterisk);
    });
  }
  return false;
};

// eg. given,
//  "urls": {
//     "100": "https://photo_url_1",
//     "1800": "https://photo_url_2"
// }
// returns https://photo_url_2
export const getPhotoWithMaxDimension = (photoUrlHash) => {
  if (!isObject(photoUrlHash)) {
    return '';
  }
  const key = Object.keys(photoUrlHash).reduce((a, b) => (a > b ? a : b));
  return photoUrlHash[key];
};

/**
 * Immutably merges a payload into an item in an array of objects
 * @param {array} array - An array of objects
 * @param {string|number} id - The unique id of the item in the array to update
 * @param {object} payload - the value to merge into the matched item
 * @param {object} options
 * @param {string} [options.idName=id] - the key of the unique identifier
 * @param {bool} [options.upsert=false] - if true, will insert the payload into the array as a new item if no matching item is found
 * @return {array} - A copy of the original array with the matching item updated
 */
export function arrayUpdateItemById(
  array,
  id,
  payload,
  { idName, upsert } = {}
) {
  let itemFound = false;
  const property = idName || 'id';
  const mappedArray = array.map((item) => {
    itemFound = itemFound || item[property] === id;
    return item[property] === id ? { ...item, ...payload } : item;
  });

  return itemFound || !upsert ? mappedArray : [...array, payload];
}

/**
 * Immutably removes an item from an array by index
 * @param {array} array
 * @param {number} index - the index of the item to remove
 * @return {array} - A copy of the original array with the matching item removed
 */
export function arrayRemoveByIndex(array, index) {
  return index === -1
    ? array
    : [...array.slice(0, index), ...array.slice(index + 1)];
}

/**
 * Immutably removes an item from an array of objects
 * @param {array} array - An array of objects
 * @param {string|number} id - The unique id of the item in the array to remove
 * @param {string} [idName=id] - the key of the unique identifier
 * @return {array} - A copy of the original array with the matching item removed
 */

export function arrayRemoveItemById(array, propertyValue, idName = 'id') {
  const index = array.findIndex((item) => item[idName] === propertyValue);
  return arrayRemoveByIndex(array, index);
}

export const capitalizeFirstLetter = (string) =>
  `${string.charAt(0).toUpperCase()}${string.slice(1)}`;

/**
 * @description Converts Object keys from string of any case to camelCase.
 * Handles nested objects and arrays.
 * */
export const convertKeysToCamel = (object) => {
  if (Array.isArray(object)) {
    return object.map(convertKeysToCamel);
  }

  return Object.keys(object).reduce((o, k) => {
    const value = object[k];

    if (isObject(value) && !Array.isArray(value)) {
      return {
        ...o,
        [camelCase(k)]: convertKeysToCamel(value)
      };
    }
    if (Array.isArray(value)) {
      return {
        ...o,
        [camelCase(k)]: [...value.map(convertKeysToCamel)]
      };
    }

    return {
      ...o,
      [camelCase(k)]: value
    };
  }, {});
};

/**
 * Checks to see if localStorage is available
 *
 * @return {boolean} - true if localStorage can be used
 */
export const isLocalStorageAvailable = () => {
  const test = 'test';
  try {
    localStorage.setItem(test, test);
    localStorage.removeItem(test);
    return true;
  } catch (e) {
    return false;
  }
};

/**
 * Appends querystring params to a URL
 *
 * @param {string} originalUrl - The original URL to append params to
 * @param {object} params - An object of key value parameters to append to URL
 * @return {string} - URL with params appended
 */
export const addParamsToURL = (originalUrl, params) => {
  const url = new URL(originalUrl);

  Object.entries(params).forEach(([key, value]) => {
    url.searchParams.append(key, value);
  });

  return url.toString();
};

export default {
  displayName,
  generateRandomId,
  isMobile,
  getMobileOS,
  getBrowser,
  isUniversalLinkPage,
  getPhotoWithMaxDimension,
  capitalizeFirstLetter,
  convertKeysToCamel,
  isLocalStorageAvailable,
  addParamsToURL
};
